import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { DEFAULT_PORTAL_TARGET } from "./portalConstants";

import type { PortalProps } from "./types";

/**
 * A wrapper component to allow for mounting it&#x27;s children to an arbitrary DOM node. Defaults to the root
 */
const Portal = ({ target = DEFAULT_PORTAL_TARGET, children }: PortalProps) => {
  const [targetElement, setTargetElement] = useState<HTMLElement>();

  useEffect(() => {
    let id = DEFAULT_PORTAL_TARGET;
    let element: HTMLElement | null = null;

    if (typeof target === "string") {
      id = target;
      element = document.getElementById(target);
    } else {
      element = target;
    }

    let systemCreated = false;

    if (!element) {
      systemCreated = true;

      const wrapperElement = document.createElement("div");

      wrapperElement.setAttribute("id", id);
      wrapperElement.setAttribute("data-testid", id);

      document.body.appendChild(wrapperElement);

      element = wrapperElement;
    }

    setTargetElement(element);

    return () => {
      if (systemCreated && element?.parentNode && !element?.children?.length) {
        element.parentNode.removeChild(element);
      }
    };
  }, [target]);

  return targetElement ? createPortal(children, targetElement) : null;
};

export default Portal;
